import { Link } from '@blaze-cms/nextjs-components';
import Button from '@blaze-react/button';
import { Checkbox } from '@blaze-react/checkboxes';
import Input from '@blaze-react/input';
import Select from '@blaze-react/select';
import Textarea from '@blaze-react/text-area';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { DATA_LAYER_OPTIONS, MESSAGE_TYPE_TITLES, MESSAGE_TYPES, PRIVACY } from '../../constants';
import { checkValidEmail, RESIDENCE_COUNTRIES } from '../../utils';
import { buildTransactionId, pushPurchase } from '../../utils/data-layer';
import { extractListingDataLayerData, filterContacts } from '../../utils/transformers';
import PortalAdapter from '../PortalAdapter';

const countryOfResidenceOptions = [
  ['', '* Your country of residence'],
  ...RESIDENCE_COUNTRIES.map(country => [country, country])
];

const MessageButtonContent = ({
  close,
  contacts,
  gtmId,
  handleSubmit,
  messageType,
  usePhone,
  yachtData
}) => {
  const handleClickOnBrokerLogo = contact => {
    const dataLayerPayload = extractListingDataLayerData(yachtData, contacts);

    const personalizedPayload = {
      ...dataLayerPayload,
      brokerIds: contact.brokerId,
      brokerNames: contact.brokerName
    };

    const transactionId = buildTransactionId(yachtData.id);
    pushPurchase(personalizedPayload, DATA_LAYER_OPTIONS.WEBSITE, transactionId);
  };

  const filteredContacts = filterContacts(contacts, true);

  const [values, setValues] = useState({
    countryOfResidence: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    newsletter: false
  });

  const { countryOfResidence, firstName, lastName, email, phone, message, newsletter } = values;
  let isFormFull = !!(
    countryOfResidence &&
    firstName &&
    lastName &&
    checkValidEmail(email) &&
    message
  );
  if (usePhone) {
    isFormFull = !!(firstName && lastName && checkValidEmail(email) && phone && message);
  }

  const buttonClass = classnames('ecommerce__form__submit', {
    'ecommerce__form__submit--disabled': !isFormFull
  });

  const handleChange = ({ target: { name: targetName } }, value) => {
    const toSave = targetName === 'newsletter' ? value.checked : value;

    setValues({
      countryOfResidence,
      firstName,
      lastName,
      email,
      phone,
      message,
      newsletter,
      [targetName]: toSave
    });
  };

  return (
    <PortalAdapter>
      <div className="ecommerce__form__overlay">
        <div className="ecommerce__form__modal">
          <div className="ecommerce__form">
            <div role="button" className="ecommerce__form__close" onClick={close}>
              <i className="fas fa-times" />
            </div>

            <div className="ecommerce__form__header">
              <div className="ecommerce__form__title-wrapper">
                <h2 className="ecommerce__form__title">{MESSAGE_TYPE_TITLES[messageType]}</h2>
              </div>

              <div
                className={`ecommerce__form__logos ecommerce__form__logos--${filterContacts.length > 2 ? 'large' : 'small'}`}>
                {filteredContacts.map((contact, i) => (
                  <a
                    href={contact.website}
                    key={i}
                    onClick={() => handleClickOnBrokerLogo(contact)}
                    className="image__wrapper"
                    target="_blank"
                    rel="noreferrer">
                    <img src={contact.image.url} alt={contact.image.alt} className="image" />
                  </a>
                ))}
              </div>
            </div>

            <form className="ecommerce__form__body">
              <div className="ecommerce__form__first-and-last-name">
                <Input
                  type="text"
                  name="firstName"
                  placeholder=" * Your first name"
                  className="ecommerce__form__input"
                  required="required"
                  onChange={({ event, value }) => handleChange(event, value)}
                  value={firstName}
                />

                <Input
                  type="text"
                  name="lastName"
                  placeholder=" * Your last name"
                  className="ecommerce__form__input"
                  required="required"
                  onChange={({ event, value }) => handleChange(event, value)}
                  value={lastName}
                />
              </div>

              {usePhone && (
                <Input
                  type="text"
                  name="phone"
                  placeholder=" * Your phone"
                  className="ecommerce__form__input"
                  required="required"
                  onChange={({ event, value }) => handleChange(event, value)}
                  value={phone}
                />
              )}

              <Input
                type="text"
                name="email"
                placeholder=" * Your email"
                className="ecommerce__form__input"
                required="required"
                onChange={({ event, value }) => handleChange(event, value)}
                value={email}
              />

              {!usePhone && (
                <Select
                  name="countryOfResidence"
                  className="ecommerce__form__select"
                  options={countryOfResidenceOptions}
                  onChange={({ event, value }) => handleChange(event, value)}
                  selected={countryOfResidence}
                />
              )}

              <Textarea
                rows="4"
                cols="50"
                placeholder=" * Your message"
                name="message"
                className="ecommerce__form__textarea"
                label=""
                required={!message}
                onChange={({ event, value }) => handleChange(event, value)}
                data-testid="textarea"
                value={message}
              />

              {!usePhone && (
                <Checkbox
                  checked={newsletter}
                  label="I would like to receive the BOAT Briefing brokerage newsletter"
                  name="newsletter"
                  id="register-newsletter"
                  onChange={({ event, value }) => handleChange(event, value)}
                />
              )}

              <Button
                type="button"
                name="button"
                data-gtm-id={gtmId}
                className={buttonClass}
                disabled={!isFormFull}
                onClick={() => handleSubmit(values, setValues)}>
                Submit
              </Button>

              <p className="ecommerce__form__disclaimer">
                The information you provide in this request form will be sent directly to the
                broker. We will not share your information with any other person. View full{' '}
                <Link className="ecommerce__form__privacy-link" href={PRIVACY.HREF}>
                  Privacy Policy
                </Link>{' '}
                for further information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </PortalAdapter>
  );
};

MessageButtonContent.propTypes = {
  close: PropTypes.func.isRequired,
  contacts: PropTypes.array.isRequired,
  gtmId: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  messageType: PropTypes.string,
  usePhone: PropTypes.bool,
  yachtData: PropTypes.object.isRequired
};

MessageButtonContent.defaultProps = {
  gtmId: 'broker-form-standard',
  messageType: MESSAGE_TYPES.GENERAL,
  usePhone: false
};

export default MessageButtonContent;
